import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import useModal from "hooks/modals/useModal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { TFacilityStatus } from "redux/reducers/models/facility";
import EmptyStatus from "../../emptyStatus/EmptyStatus";
import "./courseStatus.scss";
import StatusModal, { IFacilityStatusState } from "elements/teesheet/statisticsbar/StatusModal";
import { PostFacilityStatus, TPostFacilityStatus } from "api/rpc/2024-04/facilityAdmin/facility/status";
import { formatDate } from "helpers/Helpers";
import { showError, showSuccess } from "redux/actions/ui";
import { StatusCode } from "api/protocols";

interface IProps {
  statuses: Array<TFacilityStatus>;
  date: Date;
  handleRefresh: () => void;
}

export default function CourseStatus(props: IProps) {
  const { statuses, date, handleRefresh } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { state: viewAllModal, updateModal: updateViewAllModal, closeModal: closeViewAllModal } = useModal();
  const {
    state: statusModal,
    updateModal: updateStatusModal,
    closeModal: closeStatusModal,
  } = useModal<IFacilityStatusState>({
    date: null,
    inputData: [] as Array<{ status_input_id: number; value: string }>,
  });
  const history = useHistory();
  const { authStore } = useAppSelector(store => store);

  async function saveFacilityStatus() {
    const { ...rest } = statusModal;
    const params: TPostFacilityStatus = {
      date: formatDate(rest.date),
      input_data: rest.inputData,
    };

    const res = await PostFacilityStatus(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving facility status.")));
      return;
    }

    void handleRefresh();
    dispatch(showSuccess(t("Successfully saved the facility status.")));
    closeStatusModal();
  }

  return (
    <>
      <Card
        title={t("Course Status")}
        titleActions={[
          {
            content: t("View All"),
            action: () => updateViewAllModal({ isOpen: true }),
            disabled: !statuses || statuses?.length === 0,
          },
          ...(authStore?.user?.permissions?.edit_daily_status ? [{
            content: t("Edit"),
            action: () => updateStatusModal({ isOpen: true, date: date }),
          }] : []),
        ]}
      >
        <Card.Section>
          {!statuses ? (
            <div style={{ height: "40px", display: "flex", width: "40px", margin: "auto" }}>
              <Spin />
            </div>
          ) : statuses?.length === 0 ? (
            <EmptyStatus
              title={t("No Statuses Set (Yet)")}
              description={t("Add today's update for everyone to stay informed.")}
              action={!authStore?.user?.permissions?.tee_sheet_starter && authStore?.user?.permissions?.edit_daily_status ? {
                label: t("View daily status"),
                onClick: () => {
                  history.push("/admin/teesheet", {
                    params: { autoOpenDailyStatus: true, teeSheetDate: date },
                  });
                },
              } : undefined}
              icon={faLoader}
            />
          ) : (
            <div className="ui-course-status">
              {statuses
                ?.filter((_, index) => index < 4)
                ?.map((facilityStatus, index) => {
                  return (
                    <div key={index} className="ui-course-status-card">
                      <p className="ui-course-status-text-primary">{facilityStatus?.label}</p>
                      <p className="ui-course-status-text-secondary">{facilityStatus?.value}</p>
                    </div>
                  );
                })}
            </div>
          )}
        </Card.Section>
      </Card>
      <Sheet
        open={viewAllModal.isOpen}
        title={t("View All Course Statuses")}
        size="medium"
        onCancel={closeViewAllModal}
        backDropCancel
      >
        <DataTable columns={[{ label: t("Title") }, { label: t("Status") }]}>
          {statuses?.map((facilityStatus, index) => {
            return (
              <tr key={index}>
                <td>{facilityStatus?.label}</td>
                <td>{facilityStatus?.value}</td>
              </tr>
            );
          })}
        </DataTable>
      </Sheet>

      <StatusModal
        state={statusModal}
        update={updateStatusModal}
        close={closeStatusModal}
        onOk={() => void saveFacilityStatus()}
      />
    </>
  );
}
